/**
 * docImportance — DAT-4 (Document Applicability Transparency pack).
 *
 * Doc-first importance index. Surfaces the "why is this doc important?"
 * answer the Documents tab cannot today: aggregates the classifier
 * relevance score, the requirements the doc fulfils, the SED slots it
 * populates, recency, and the user's manual priority star into a
 * single auditable 0..1 number.
 *
 * Design rules:
 *
 *   - **Pure / side-effect free.** Safe to call on every render; safe
 *     to unit-test without React.
 *   - **No new persisted field.** Importance is DERIVED at read time
 *     from existing SiteDocument fields (relevance, populatedSlotCount,
 *     fulfilledRequirementIds, priority, uploadedAt). A persisted cache
 *     is a deferred follow-up (DAT-FOLLOWUP-PERSIST-IMPORTANCE).
 *   - **Visible weights.** The blend is a small set of named exports
 *     so users (and reviewers) can argue with the math directly. If a
 *     weight drifts, every site re-ranks predictably — no black box.
 *   - **Tolerant of legacy docs.** Every contributing field is
 *     optional; missing values fall back to 0 cleanly so docs uploaded
 *     before UPE / URE still get a finite (low) importance.
 *
 * Composes with:
 *
 *   - **UPE pack** — reads `doc.populatedSlotCount` +
 *     `doc.fulfilledRequirementIds`.
 *   - **DCT/DRT** — reads per-report-type `doc.relevance` scores.
 *   - **docSedReverseIndex** — used as a live fallback for the
 *     slot-population signal when UPE's cached count is missing
 *     (R2 mitigation in the pack).
 */
import type { SiteDocument } from '../types/siteRag';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { buildReverseIndex } from './docSedReverseIndex';

/* ── Weights (visible, auditable) ─────────────────────────────── */

/**
 * Weight on the maximum DCT relevance score across report types.
 * Strongest signal — a 1.0 max relevance contributes 0.40 to the
 * importance index by itself.
 */
export const WEIGHT_MAX_DCT = 0.40;
/**
 * Weight on the number of distinct requirements the doc fulfils,
 * normalised by `REQUIREMENT_SATURATION` (a doc that fulfils 5+
 * requirements saturates this term at 0.25).
 */
export const WEIGHT_REQS_SERVED = 0.25;
/**
 * Weight on the number of SED slots populated, normalised by
 * `SLOT_SATURATION` (a doc that populated 10+ slots saturates at
 * 0.20).
 */
export const WEIGHT_SLOTS_POPULATED = 0.20;
/**
 * Weight on a linear recency decay over `RECENCY_WINDOW_DAYS` (docs
 * uploaded today contribute 0.10; docs uploaded 1+ year ago
 * contribute 0).
 */
export const WEIGHT_RECENCY = 0.10;
/**
 * Flat additive boost when the user has manually starred the doc.
 * Generally enough to cluster starred docs near the top of an
 * importance-sorted list without overwhelming a high-DCT-low-priority
 * doc.
 */
export const PRIORITY_BOOST = 0.15;

/** Number of requirements at which the `reqsServed` term saturates. */
export const REQUIREMENT_SATURATION = 5;
/** Number of populated SED slots at which the slots term saturates. */
export const SLOT_SATURATION = 10;
/** Days over which the recency boost decays linearly to zero. */
export const RECENCY_WINDOW_DAYS = 365;

const MS_PER_DAY = 86_400_000;

/* ── Helper projections ───────────────────────────────────────── */

/**
 * Max DCT relevance across every report type the classifier scored.
 * Returns 0 for legacy docs without a `relevance` map and for docs
 * whose every entry is non-numeric.
 */
export function maxDctRelevance(doc: Pick<SiteDocument, 'relevance'>): number {
  const values = Object.values(doc.relevance ?? {});
  let max = 0;
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v) && v > max) max = v;
  }
  return max;
}

/**
 * Count SED slots the doc populates by reading the doc → SED reverse
 * index. Used as a live fallback when `doc.populatedSlotCount` is
 * missing or stale (R2 in the pack — UPE writes the cached count, but
 * the SED edit could have moved out from under it).
 */
export function countDocSlotsFromSed(docId: string, sed: SiteEvidenceDocument | undefined): number {
  if (!sed) return 0;
  const idx = buildReverseIndex(sed);
  return idx.get(docId)?.length ?? 0;
}

/**
 * Recency boost over a linear 1-year decay. `uploadedAt` is an ISO
 * timestamp on SiteDocument; missing / malformed → 0 (treated as
 * old, no boost). Negative ages (clock drift, future timestamps) are
 * clamped to "today" = boost 1.
 */
export function recencyBoost(uploadedAt: string | undefined, now: number = Date.now()): number {
  if (!uploadedAt) return 0;
  const t = Date.parse(uploadedAt);
  if (!Number.isFinite(t)) return 0;
  const ageDays = (now - t) / MS_PER_DAY;
  if (ageDays <= 0) return 1;
  if (ageDays >= RECENCY_WINDOW_DAYS) return 0;
  return 1 - ageDays / RECENCY_WINDOW_DAYS;
}

/* ── Breakdown projection (UI consumes) ───────────────────────── */

/**
 * Per-term breakdown of the importance computation. Exposed so the
 * DocLineageInspector can show the user EXACTLY how each piece
 * contributed — that's the customer-trust answer to "how are you
 * deciding which docs are important?".
 */
export interface DocImportanceBreakdown {
  /** Final 0..1 importance, clamped. */
  importance: number;
  maxDct: number;
  reqsServed: number;
  slotsPopulated: number;
  recency: number;
  priority: boolean;
  /** Each term's CONTRIBUTION (after weighting / saturation). */
  contributions: {
    dct: number;
    reqs: number;
    slots: number;
    recency: number;
    priority: number;
  };
}

/**
 * Build the per-term breakdown for a doc. Pure projection — no
 * side effects, deterministic given the inputs.
 *
 * Slot count resolution order:
 *   1. `doc.populatedSlotCount` (UPE cached value).
 *   2. `countDocSlotsFromSed(doc.id, sed)` (live derivation).
 *   3. 0 if neither is available.
 */
export function describeDocImportance(
  doc: SiteDocument,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): DocImportanceBreakdown {
  const maxDct = maxDctRelevance(doc);
  const reqsServed = (doc.fulfilledRequirementIds ?? []).length;
  const cachedSlots = typeof doc.populatedSlotCount === 'number' && doc.populatedSlotCount > 0
    ? doc.populatedSlotCount
    : countDocSlotsFromSed(doc.id, sed);
  const slotsPopulated = cachedSlots;
  const recency = recencyBoost(doc.uploadedAt, now);
  const priority = !!doc.priority;

  const dctContribution = WEIGHT_MAX_DCT * maxDct;
  const reqsContribution = WEIGHT_REQS_SERVED * Math.min(1, reqsServed / REQUIREMENT_SATURATION);
  const slotsContribution = WEIGHT_SLOTS_POPULATED * Math.min(1, slotsPopulated / SLOT_SATURATION);
  const recencyContribution = WEIGHT_RECENCY * recency;
  const priorityContribution = priority ? PRIORITY_BOOST : 0;

  const raw = dctContribution + reqsContribution + slotsContribution + recencyContribution + priorityContribution;
  const importance = Math.max(0, Math.min(1, raw));

  return {
    importance,
    maxDct,
    reqsServed,
    slotsPopulated,
    recency,
    priority,
    contributions: {
      dct: dctContribution,
      reqs: reqsContribution,
      slots: slotsContribution,
      recency: recencyContribution,
      priority: priorityContribution,
    },
  };
}

/**
 * Scalar importance — shorthand for `describeDocImportance(...).importance`.
 * Useful for sorters that don't need the breakdown.
 */
export function computeDocImportance(
  doc: SiteDocument,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): number {
  return describeDocImportance(doc, sed, now).importance;
}

/**
 * DAT-FOLLOWUP-PERSIST-IMPORTANCE — O(1) score reader.
 *
 * Returns the persisted `doc.importanceScore` when present (the
 * common, fast path); falls back to `computeDocImportance(...)` only
 * when the doc has never been scored. The fallback path is what
 * fires for legacy docs on the first mount post-deploy AND for newly
 * uploaded docs whose upload pipeline hasn't yet stamped the score.
 *
 * The caller (`DocumentLibrary`) is responsible for persisting the
 * fallback value via `patchSiteDocument` — this helper is pure
 * (no I/O). See `selectDocsNeedingImportanceBackfill` for the
 * "what to write" projection.
 */
export function getOrComputeImportance(
  doc: SiteDocument,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): number {
  if (typeof doc.importanceScore === 'number' && Number.isFinite(doc.importanceScore)) {
    return doc.importanceScore;
  }
  return computeDocImportance(doc, sed, now);
}

/**
 * DAT-FOLLOWUP-PERSIST-IMPORTANCE — backfill projection.
 *
 * Returns ONLY the docs whose `importanceScore` is currently
 * `undefined`. Idempotency-critical: the caller writes one
 * `patchSiteDocument` per entry, and the gate here is the contract
 * that prevents a duplicate write on a doc that's already scored.
 *
 * The projection is pure — it never mutates the input array.
 *
 * Returned entries carry the computed score so the caller doesn't
 * have to recompute (and so the unit test can pin the score that
 * would be persisted without poking at internals).
 */
export interface ImportanceBackfillEntry {
  docId: string;
  importance: number;
}

export function selectDocsNeedingImportanceBackfill(
  docs: ReadonlyArray<SiteDocument>,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): ImportanceBackfillEntry[] {
  const out: ImportanceBackfillEntry[] = [];
  for (const d of docs) {
    if (typeof d.importanceScore === 'number' && Number.isFinite(d.importanceScore)) {
      continue;
    }
    out.push({ docId: d.id, importance: computeDocImportance(d, sed, now) });
  }
  return out;
}

/**
 * DAT-FOLLOWUP-PERSIST-IMPORTANCE — input-field set whose mutation
 * invalidates the cached importance score.
 *
 * `patchSiteDocument` consults this list: when a patch touches any
 * of these fields, it auto-clears `importanceScore` (and
 * `importanceScoreAt`) so the next render re-derives via the
 * fallback path. Keeping the list in this module makes the
 * invalidation discoverable from the same file that owns the
 * formula — drift between "what the formula reads" and "what the
 * persist layer invalidates on" is a category bug we want one
 * source of truth for.
 *
 * `uploadedAt` is omitted: it shifts on every mount-time backfill?
 * No — `uploadedAt` is the original upload moment, never patched.
 * It is read by `recencyBoost` but stable; safe to leave out.
 */
export const IMPORTANCE_INPUT_FIELDS: ReadonlyArray<keyof SiteDocument> = [
  'relevance',
  'populatedSlotCount',
  'fulfilledRequirementIds',
  'priority',
] as const;

/**
 * Returns true iff the patch touches at least one input field the
 * importance formula reads. Pure projection — used by
 * `patchSiteDocument` to decide whether to auto-clear the cached
 * score on a write.
 */
export function patchInvalidatesImportance(patch: Partial<SiteDocument>): boolean {
  for (const f of IMPORTANCE_INPUT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, f)) return true;
  }
  return false;
}

/* ── Ranking ──────────────────────────────────────────────────── */

export interface RankedDoc {
  doc: SiteDocument;
  importance: number;
  /** 1-based rank (1 = most important). */
  rank: number;
}

/**
 * Sort docs by importance desc; assign a 1-based rank. Ties are
 * broken by `uploadedAt` desc (newer first), then `fileName` asc
 * so the result is deterministic across renders.
 *
 * Does NOT mutate the input array.
 */
export function rankDocsByImportance(
  docs: ReadonlyArray<SiteDocument>,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): RankedDoc[] {
  // DAT-FOLLOWUP-PERSIST-IMPORTANCE — prefer the persisted score
  // when present (O(1)); fall back to `computeDocImportance` for
  // legacy / pre-backfill docs.
  const scored = docs.map(d => ({ doc: d, importance: getOrComputeImportance(d, sed, now) }));
  scored.sort((a, b) => {
    if (b.importance !== a.importance) return b.importance - a.importance;
    const ta = Date.parse(a.doc.uploadedAt) || 0;
    const tb = Date.parse(b.doc.uploadedAt) || 0;
    if (tb !== ta) return tb - ta;
    return a.doc.fileName.localeCompare(b.doc.fileName);
  });
  return scored.map((s, i) => ({ ...s, rank: i + 1 }));
}

/**
 * Build a Map<docId, RankedDoc> for O(1) per-row lookup. Convenience
 * for the DocumentLibrary row renderer when sort-by-importance is on
 * (DAT-5).
 */
export function rankDocsByImportanceMap(
  docs: ReadonlyArray<SiteDocument>,
  sed: SiteEvidenceDocument | undefined,
  now: number = Date.now(),
): Map<string, RankedDoc> {
  const out = new Map<string, RankedDoc>();
  for (const r of rankDocsByImportance(docs, sed, now)) {
    out.set(r.doc.id, r);
  }
  return out;
}
